import { useEffect, useMemo, useState } from "react";
import { testDecode } from "../../api/hooks";
import type { ParameterDef, SampleDef, SegmentDef, TestDecodeResult } from "../../api/types";
import { Sparkline } from "../../components/Sparkline";
import { errText } from "../../components/ui";

export const FUNCTIONS = ["Linear", "BCD", "Octal", "Interpolation", "DateTime", "ISO #5", "Text", "Synchro", "Binary"];

export function blankParameter(mnemonic = ""): ParameterDef {
  return {
    mnemonic,
    name: null,
    units: null,
    decimal_places: null,
    function: "Linear",
    coefficients: [0, 1],
    sign_bit: null,
    signed: false,
    data_points: null,
    text_table: null,
    samples: [{ segments: [{ word: 1, lsb: 1, msb: 12, subframes: [1, 2, 3, 4] }] }],
    conversions: [],
    superframe: null,
    is_superframe_counter: false,
    sf_num_steps: null,
    sf_step_size: null,
    min_op_range: null,
    max_op_range: null,
    signal_source: null,
    signal_type: null,
    comments: null,
    origin: "manual",
    extra: {},
  };
}

const numList = (s: string): number[] =>
  s
    .split(/[,\s]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isFinite(n));

const optNum = (s: string): number | null => (s.trim() === "" ? null : Number.isFinite(Number(s)) ? Number(s) : null);

export interface ParameterEditorProps {
  projectId: string;
  frameName: string;
  wordsPerSecond: number | null | undefined;
  value: ParameterDef;
  original: ParameterDef | null;
  isOverride: boolean;
  isNew: boolean;
  flightId: string | null;
  flightLabel: string;
  saving: boolean;
  onChange: (p: ParameterDef) => void;
  onSave: () => void;
  onRevert: () => void;
  onCancel: () => void;
}

export function ParameterEditor(props: ParameterEditorProps) {
  const { projectId, frameName, wordsPerSecond, value, original, isOverride, isNew, flightId, flightLabel, saving, onChange, onSave, onRevert, onCancel } = props;
  const [test, setTest] = useState<TestDecodeResult | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [jsonDraft, setJsonDraft] = useState<string | null>(null);
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const [autoTest, setAutoTest] = useState(true);

  const set = <K extends keyof ParameterDef>(k: K, v: ParameterDef[K]) => onChange({ ...value, [k]: v });

  const problems = useMemo(() => validate(value, wordsPerSecond ?? null), [value, wordsPerSecond]);
  const rate = useMemo(() => {
    if (!value.samples.length) return 0;
    const sf = value.samples[0].segments[0]?.subframes?.length ?? 4;
    return (value.samples.length * sf) / 4;
  }, [value.samples]);
  const totalBits = useMemo(() => value.samples[0]?.segments.reduce((a, s) => a + (s.msb - s.lsb + 1), 0) ?? 0, [value.samples]);

  const runTest = async () => {
    if (!flightId || problems.length) return;
    setTesting(true);
    setTestErr(null);
    try {
      setTest(await testDecode(projectId, { flight_id: flightId, frame: frameName, parameter: value, points: 400 }));
    } catch (e) {
      setTest(null);
      setTestErr(errText(e));
    } finally {
      setTesting(false);
    }
  };

  // debounce test decode as the definition changes
  const sig = JSON.stringify(value);
  useEffect(() => {
    if (!autoTest || !flightId || problems.length) return;
    const h = setTimeout(() => void runTest(), 450);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, flightId, autoTest, frameName]);

  const updateSegment = (si: number, gi: number, patch: Partial<SegmentDef>) => {
    const samples = value.samples.map((s, i) =>
      i !== si ? s : { ...s, segments: s.segments.map((g, j) => (j !== gi ? g : { ...g, ...patch })) },
    );
    set("samples", samples);
  };
  const addSegment = (si: number) => {
    const last = value.samples[si].segments.at(-1);
    const seg: SegmentDef = { word: (last?.word ?? 0) + 1, lsb: 1, msb: 12, subframes: last?.subframes ?? [1, 2, 3, 4] };
    set("samples", value.samples.map((s, i) => (i !== si ? s : { ...s, segments: [...s.segments, seg] })));
  };
  const removeSegment = (si: number, gi: number) =>
    set("samples", value.samples.map((s, i) => (i !== si ? s : { ...s, segments: s.segments.filter((_, j) => j !== gi) })));
  const addSample = () => {
    const last = value.samples.at(-1);
    const seg = last?.segments[0];
    const sample: SampleDef = {
      segments: [{ word: (seg?.word ?? 0) + Math.max(1, Math.floor((wordsPerSecond ?? 64) / Math.max(1, value.samples.length + 1))), lsb: seg?.lsb ?? 1, msb: seg?.msb ?? 12, subframes: seg?.subframes ?? [1, 2, 3, 4] }],
    };
    set("samples", [...value.samples, sample]);
  };
  const removeSample = (si: number) => set("samples", value.samples.filter((_, i) => i !== si));

  const applyJson = () => {
    if (jsonDraft === null) return;
    try {
      const parsed = JSON.parse(jsonDraft) as ParameterDef;
      onChange({ ...blankParameter(), ...parsed });
      setJsonErr(null);
      setJsonDraft(null);
    } catch (e) {
      setJsonErr(errText(e));
    }
  };

  const diff = (k: keyof ParameterDef) => (original && JSON.stringify(original[k]) !== JSON.stringify(value[k]) ? " changed" : "");

  return (
    <div className="param-editor">
      <div className="pane-head">
        <span className="mono">{value.mnemonic || "new parameter"}</span>
        {isNew ? <span className="badge info">new</span> : null}
        {isOverride ? <span className="badge warn" title="PRM definition is replaced by this override">override</span> : null}
        {value.origin === "manual" && !isNew ? <span className="badge">manual</span> : null}
        <span className="spacer" />
        <button className="btn sm ghost" onClick={() => setShowJson((s) => !s)} title="Edit the full definition as JSON">
          {showJson ? "Form" : "JSON"}
        </button>
        <button className="btn sm" onClick={onCancel}>
          Close
        </button>
      </div>
      <div className="pane-body col param-form">
        {showJson ? (
          <>
            <textarea
              className="inp mono"
              style={{ minHeight: 320, fontSize: 12 }}
              value={jsonDraft ?? JSON.stringify(value, null, 2)}
              onChange={(e) => setJsonDraft(e.target.value)}
              spellCheck={false}
            />
            {jsonErr ? <div className="err small">{jsonErr}</div> : null}
            <div className="row">
              <button className="btn sm primary" disabled={jsonDraft === null} onClick={applyJson}>
                Apply JSON
              </button>
              <button className="btn sm" disabled={jsonDraft === null} onClick={() => (setJsonDraft(null), setJsonErr(null))}>
                Discard
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="grid2">
              <label className={`field${diff("mnemonic")}`}>
                <span>Mnemonic</span>
                <input className="inp mono" value={value.mnemonic} onChange={(e) => set("mnemonic", e.target.value.trim())} placeholder="ALT" />
              </label>
              <label className={`field${diff("name")}`}>
                <span>Name</span>
                <input className="inp" value={value.name ?? ""} onChange={(e) => set("name", e.target.value || null)} placeholder="Pressure altitude" />
              </label>
              <label className={`field${diff("units")}`}>
                <span>Units</span>
                <input className="inp" value={value.units ?? ""} onChange={(e) => set("units", e.target.value || null)} placeholder="FEET" />
              </label>
              <label className={`field${diff("function")}`}>
                <span>Function</span>
                <select className="inp" value={value.function} onChange={(e) => set("function", e.target.value)}>
                  {FUNCTIONS.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </label>
              <label className={`field${diff("coefficients")}`} title="a0, a1, a2 … : EU = a0 + a1·raw + a2·raw² …">
                <span>Coefficients</span>
                <input className="inp mono" value={value.coefficients.join(", ")} onChange={(e) => set("coefficients", numList(e.target.value))} placeholder="0, 1" />
              </label>
              <label className={`field${diff("sign_bit")}`} title="Two's complement sign bit index (blank = unsigned)">
                <span>Sign bit</span>
                <input className="inp mono" value={value.sign_bit ?? ""} onChange={(e) => set("sign_bit", optNum(e.target.value))} placeholder="—" />
              </label>
              <label className={`field${diff("superframe")}`} title="Only decode when the superframe counter equals this cycle (1..16)">
                <span>Superframe</span>
                <input className="inp mono" value={value.superframe ?? ""} onChange={(e) => set("superframe", optNum(e.target.value))} placeholder="—" />
              </label>
              <label className="field" title="Operating range (for validation badges only)">
                <span>Op. range</span>
                <span className="row">
                  <input className="inp mono sm" style={{ width: 80 }} value={value.min_op_range ?? ""} onChange={(e) => set("min_op_range", optNum(e.target.value))} placeholder="min" />
                  <input className="inp mono sm" style={{ width: 80 }} value={value.max_op_range ?? ""} onChange={(e) => set("max_op_range", optNum(e.target.value))} placeholder="max" />
                </span>
              </label>
              {value.function === "Interpolation" ? (
                <label className={`field span2${diff("data_points")}`} title="raw,eu pairs; e.g. (0,-60) (1000,0) (2000,60)">
                  <span>Data points</span>
                  <input
                    className="inp mono"
                    value={(value.data_points ?? []).map(([a, b]) => `(${a},${b})`).join(" ")}
                    onChange={(e) => {
                      const pts = [...e.target.value.matchAll(/\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)/g)].map((m) => [Number(m[1]), Number(m[2])] as [number, number]);
                      set("data_points", pts.length ? pts : null);
                    }}
                  />
                </label>
              ) : null}
              {value.function === "Text" ? (
                <label className={`field span2${diff("text_table")}`} title="raw=label pairs, one per line">
                  <span>Text table</span>
                  <textarea
                    className="inp mono"
                    rows={3}
                    value={Object.entries(value.text_table ?? {})
                      .map(([k, v]) => `${k}=${v}`)
                      .join("\n")}
                    onChange={(e) => {
                      const tbl: Record<string, string> = {};
                      for (const line of e.target.value.split("\n")) {
                        const m = line.match(/^\s*(\d+)\s*=\s*(.*)$/);
                        if (m) tbl[m[1]] = m[2];
                      }
                      set("text_table", Object.keys(tbl).length ? tbl : null);
                    }}
                  />
                </label>
              ) : null}
              <label className="field span2">
                <span>Comments</span>
                <input className="inp" value={value.comments ?? ""} onChange={(e) => set("comments", e.target.value || null)} />
              </label>
              <label className="row small" style={{ gap: 6 }}>
                <input type="checkbox" checked={value.is_superframe_counter} onChange={(e) => set("is_superframe_counter", e.target.checked)} />
                superframe counter
              </label>
            </div>

            <h4 className={`row${diff("samples")}`}>
              Samples <span className="badge">{rate} Hz</span> <span className="badge">{totalBits} bits</span>
              <span className="spacer" />
              <button className="btn sm ghost" onClick={addSample}>
                ＋ sample
              </button>
            </h4>
            <div className="samples">
              {value.samples.map((s, si) => (
                <div key={si} className="sample card">
                  <div className="row small dim">
                    <span>sample {si + 1}</span>
                    <span className="spacer" />
                    <button className="btn sm ghost" onClick={() => addSegment(si)} title="Concatenate bits from another word (MSB-first)">
                      ＋ segment
                    </button>
                    {value.samples.length > 1 ? (
                      <button className="btn sm ghost danger" onClick={() => removeSample(si)}>
                        ✕
                      </button>
                    ) : null}
                  </div>
                  <table className="tbl seg-tbl">
                    <thead>
                      <tr>
                        <th>word</th>
                        <th>lsb</th>
                        <th>msb</th>
                        <th>subframes</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {s.segments.map((g, gi) => (
                        <tr key={gi}>
                          <td>
                            <input className="inp sm mono" type="number" min={1} max={wordsPerSecond ?? 8192} value={g.word} onChange={(e) => updateSegment(si, gi, { word: Number(e.target.value) })} />
                          </td>
                          <td>
                            <input className="inp sm mono" type="number" min={1} max={12} value={g.lsb} onChange={(e) => updateSegment(si, gi, { lsb: Number(e.target.value) })} />
                          </td>
                          <td>
                            <input className="inp sm mono" type="number" min={1} max={12} value={g.msb} onChange={(e) => updateSegment(si, gi, { msb: Number(e.target.value) })} />
                          </td>
                          <td>
                            <span className="row" style={{ gap: 4 }}>
                              {[1, 2, 3, 4].map((sf) => (
                                <label key={sf} className={`sf-chip${g.subframes.includes(sf) ? " on" : ""}`} title={`subframe ${sf}`}>
                                  <input
                                    type="checkbox"
                                    checked={g.subframes.includes(sf)}
                                    onChange={(e) => {
                                      const next = e.target.checked ? [...g.subframes, sf].sort() : g.subframes.filter((x) => x !== sf);
                                      if (next.length) updateSegment(si, gi, { subframes: next });
                                    }}
                                  />
                                  {sf}
                                </label>
                              ))}
                            </span>
                          </td>
                          <td>
                            {s.segments.length > 1 ? (
                              <button className="btn sm ghost danger" onClick={() => removeSegment(si, gi)}>
                                ✕
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </>
        )}

        {problems.length ? (
          <div className="card problems">
            {problems.map((p, i) => (
              <div key={i} className="err small">
                {p}
              </div>
            ))}
          </div>
        ) : null}

        <h4 className="row">
          Test decode
          <span className="spacer" />
          <label className="row small dim" style={{ gap: 4 }}>
            <input type="checkbox" checked={autoTest} onChange={(e) => setAutoTest(e.target.checked)} /> live
          </label>
          <button className="btn sm" disabled={!flightId || testing || problems.length > 0} onClick={() => void runTest()}>
            ▶ {flightLabel}
          </button>
        </h4>
        {!flightId ? <div className="dim small">Select an active flight on the Flights screen to preview decoded values.</div> : null}
        {testErr ? <div className="err small">{testErr}</div> : null}
        {test ? (
          <div className="card test-card">
            <Sparkline t={test.tile.t} v={test.tile.v} totalSeconds={test.total_seconds} width={360} height={70} title={`${test.mnemonic} preview`} />
            <div className="kv small">
              <span>range</span>
              <span className="mono">
                {fmtNum(test.min)} … {fmtNum(test.max)} {test.units ?? ""}
              </span>
              <span>mean</span>
              <span className="mono">{fmtNum(test.mean)}</span>
              <span>rate</span>
              <span className="mono">
                {test.rate} Hz · {test.samples} samples
              </span>
              <span>distinct</span>
              <span className="mono">
                {test.distinct}
                {test.values_preview ? ` (${test.values_preview.map(fmtNum).join(", ")})` : ""}
              </span>
              <span>NaN</span>
              <span className="mono">{test.nan_pct}%</span>
              <span>raw words</span>
              <span className="mono faint">{test.raw_preview.join(" ")}</span>
            </div>
            {test.problems.map((p, i) => (
              <div key={i} className="warn small">
                {p}
              </div>
            ))}
            {value.min_op_range != null && test.min != null && test.min < value.min_op_range ? <div className="warn small">min below operating range</div> : null}
            {value.max_op_range != null && test.max != null && test.max > value.max_op_range ? <div className="warn small">max above operating range</div> : null}
          </div>
        ) : null}
      </div>
      <div className="pane-foot row">
        {!isNew ? (
          <button className="btn sm danger" onClick={onRevert} title={isOverride ? "Remove the override and go back to the PRM definition" : "Delete this parameter"}>
            {isOverride ? "Revert to PRM" : "Delete"}
          </button>
        ) : null}
        <span className="spacer" />
        <button className="btn primary" disabled={saving || problems.length > 0} onClick={onSave}>
          {isNew ? "Add parameter" : "Save"}
        </button>
      </div>
    </div>
  );
}

function validate(p: ParameterDef, wps: number | null): string[] {
  const out: string[] = [];
  if (!p.mnemonic) out.push("mnemonic is required");
  else if (!/^[A-Za-z_][\w-]*$/.test(p.mnemonic)) out.push("mnemonic must be an identifier (letters, digits, _)");
  if (!p.samples.length) out.push("at least one sample is required");
  p.samples.forEach((s, si) => {
    if (!s.segments.length) out.push(`sample ${si + 1}: needs a segment`);
    s.segments.forEach((g, gi) => {
      if (!(g.word >= 1)) out.push(`sample ${si + 1} segment ${gi + 1}: word must be ≥ 1`);
      if (wps && g.word > wps) out.push(`sample ${si + 1} segment ${gi + 1}: word ${g.word} exceeds ${wps} words/second`);
      if (g.lsb < 1 || g.msb > 12 || g.lsb > g.msb) out.push(`sample ${si + 1} segment ${gi + 1}: bits must satisfy 1 ≤ lsb ≤ msb ≤ 12`);
      if (!g.subframes.length) out.push(`sample ${si + 1} segment ${gi + 1}: pick at least one subframe`);
    });
  });
  if (p.function === "Interpolation" && (p.data_points?.length ?? 0) < 2) out.push("Interpolation needs at least two data points");
  if (p.superframe != null && (p.superframe < 1 || p.superframe > 16)) out.push("superframe must be 1..16");
  return out;
}

export function fmtNum(x: number | null | undefined): string {
  if (x == null || !Number.isFinite(x)) return "—";
  if (Math.abs(x) >= 10000) return x.toFixed(0);
  if (Math.abs(x) >= 100) return x.toFixed(1);
  return x.toFixed(3).replace(/\.?0+$/, "");
}
